import { useEffect, useRef, useCallback } from 'react';
import { useAuthController } from '@/features/auth/controller/auth.controller';
import { refreshSession } from '@/services/http';
import { usesBearerAuth } from '@/services/nativeSession';
import { useGameStore } from '@/store/useGameStore';
import { useSyncStore } from '@/hooks/useSyncStore';

const REFRESH_INTERVAL_MS = 12 * 60 * 1000;
const INACTIVITY_LIMIT_MS = 15 * 60 * 1000;
/**
 * Longer than a five-set match with its breaks. `matchId` outlives « Quitter »
 * and a refused action stays queued: without a bound, a club's shared computer
 * would never sign out again. The queue survives the sign-out anyway.
 */
const MATCH_INACTIVITY_LIMIT_MS = 3 * 60 * 60 * 1000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown', 'keydown', 'touchstart', 'scroll', 'pointermove',
];

/**
 * Signing out wipes the local match state (point in progress) and stops the
 * queue from sending: an injury break or a tablet left to sleep mid-match
 * must not cost an action (VOL-324).
 */
function hasLiveMatchWork(): boolean {
  if (useGameStore.getState().matchId) return true;
  const { pendingEvents, pendingUndos } = useSyncStore.getState();
  return pendingEvents.length > 0 || pendingUndos.length > 0;
}

export function useSessionKeepAlive() {
  const user = useAuthController((s) => s.user);
  const logout = useAuthController((s) => s.logout);
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!user) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    lastActivityRef.current = Date.now();

    ACTIVITY_EVENTS.forEach((evt) => document.addEventListener(evt, markActive, { passive: true }));

    const tick = async () => {
      const idleMs = Date.now() - lastActivityRef.current;

      // Not in the app: the club's tablet lies on the bench at half-time or during
      // a video replay, and signing out there would revoke its season-long session.
      const limitMs = hasLiveMatchWork() ? MATCH_INACTIVITY_LIMIT_MS : INACTIVITY_LIMIT_MS;
      if (idleMs >= limitMs && !usesBearerAuth()) {
        await logout();
        window.location.href = '/login';
        return;
      }

      try {
        await refreshSession();
      } catch {
        // Silently ignore — the retry-on-401 mechanism handles real failures
      }
    };

    intervalRef.current = setInterval(tick, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      ACTIVITY_EVENTS.forEach((evt) => document.removeEventListener(evt, markActive));
    };
  }, [user, logout, markActive]);
}
